import lang from '@/lang'
import moment from 'moment'
import orderBy from 'lodash/orderBy'
import { uniqueId, compact } from 'lodash'
import { getLabelForCountry } from '@/config'

import config from 'config'
import { customerTypes } from '@/modules/base/util/constants'

const EMPTY_CATEGORY = 'Keine Angabe'
const EMPTY_PROP = ''

const getBirthDay = (birthday) => {
  if (!birthday) return null

  return moment(birthday).isValid()
    ? moment(birthday).format(config.dateFormat.api)
    : moment(birthday, config.dateFormat.client).format(config.dateFormat.api)
}

const getContactPersons = (contactPersons) => {
  const orderedContactPersons = orderBy(contactPersons, ['isMainContact'], ['desc'])
  return orderedContactPersons.map((contactPerson, index) => ({
    ...contactPerson,
    birthday: getBirthDay(contactPerson.birthday),
    tempId: index + 1,
  }))
}

export default class CustomerModel {
  constructor(data = {}) {
    this.id = data.id || undefined
    this.addresses = data.addresses
      ? data.addresses.map((address, index) => ({ ...address, tempId: index + 1 }))
      : []
    this.customerDefaultAddress = data.customerDefaultAddress || {}
    this.balance = data.balance
    this.birthday = data.birthday
    this.category = data.category || EMPTY_CATEGORY
    this.companyName = data.companyName || EMPTY_PROP
    this.companyNameAffix = data.companyNameAffix || EMPTY_PROP
    this.contactType = data.contactType
    this.payConditionId = data.payConditionId || 0
    this.deliveryConditionId = data.deliveryConditionId || 0
    this.discount = data.discount || 0.00
    this.email = data.email || EMPTY_PROP
    this.fax = data.fax || EMPTY_PROP
    this.firstName = data.firstName || EMPTY_PROP
    this.lastName = data.lastName || EMPTY_PROP
    this.kind = data.kind || customerTypes.company
    this.mandator = data.mandator || null
    this.mobile = data.mobile || EMPTY_PROP
    this.number = data.number
    this.name = data.name || EMPTY_PROP
    this.notes = data.notes || EMPTY_PROP
    this.notesAlert = data.notesAlert || false
    this.phone1 = data.phone1 || EMPTY_PROP
    this.phone2 = data.phone2 || EMPTY_PROP
    this.salutation = data.salutation
    this.title = data.title || EMPTY_PROP
    this.vatFree = data.vatFree
    this.vatId = data.vatId
    this.website = data.website || EMPTY_PROP
    this.vatNumber = data.vatNumber || EMPTY_PROP
    this.netPriceAsDefault = data.netPriceAsDefault || false
    this.tags = data.tags || []
    this.externalReferences = data.externalReferences || []
    this.contactPersons = getContactPersons(data.contactPersons)
    this.accountNo = data.accountNo || EMPTY_PROP
    this.hourlyRateId = data.hourlyRateId || null
    this.customValues = data.customValues || {}
    this.accountHolder = data.name || EMPTY_PROP
    this.iban = EMPTY_PROP
    this.bic = EMPTY_PROP
    this.buyerReference = data.buyerReference
    this.customerOwnSupplierNumber = data.customerOwnSupplierNumber
    this.customerReferenceNumber = data.customerReferenceNumber

    if (data.bankAccounts && data.bankAccounts?.length) {
      const { accountHolder, iban, bic } = data.bankAccounts[0]
      this.accountHolder = accountHolder || data.name
      this.iban = iban
      this.bic = bic
    }

    this.directDebitMandates = data.directDebitMandates || []
  }

  get tempId() {
    return this.id || uniqueId('temp')
  }

  get displayName() {
    // when a `name` is given by server, use it as `displayName`
    if (this.name) {
      return this.name
    }
    // otherwise, calculate `displayName` from `custKind`, `title`, `name1`, `name2` and `name3`
    let displayName
    displayName += this.title ? `${this.title} ` : ''
    displayName += (this.kind === customerTypes.PERSON) ? `${this.firstName} ${this.lastName}` : `${this.companyName} ${this.companyNameAffix}`
    return displayName
  }

  get billingAddressCountry() {
    const country = getLabelForCountry(this.customerDefaultAddress.billingAddress.countryIso)
    return country.label
  }

  get deliveryAddressCountry() {
    if (!this.customerDefaultAddress.deliveryAddress) return
    const country = getLabelForCountry(this.customerDefaultAddress.deliveryAddress.countryIso)
    return country.label
  }

  get custNoString() {
    return `${this.kind === customerTypes.PERSON ? lang.customer.model.person : lang.customer.model.company} | ${lang.customer.model.customerNumberAbbreviation} ${this.number || ''}`
  }

  get isPerson() {
    return (this.kind === customerTypes.PERSON)
  }

  get websiteLink() {
    if (/https?:\/\//.test(this.website)) {
      return this.website
    }

    return `https://${this.website}`
  }

  get mapData() {
    const addressToParse = this.customerDefaultAddress.billingAddress
    const address = compact([addressToParse.street, addressToParse.zipCode, addressToParse.city, this.billingAddressCountry])
    const subHeadingZipCity = compact([addressToParse.zipCode, addressToParse.city]).join(' ')
    let subHeading = subHeadingZipCity && this.billingAddressCountry ? `${subHeadingZipCity}, ${this.billingAddressCountry}` : ''
    subHeading = subHeading || subHeadingZipCity || this.billingAddressCountry

    return {
      showMap: address.join('').length > 0,
      heading: addressToParse.street,
      subHeading,
      mapAddress: address.join(', '),
    }
  }
}
